<template>
  <NavBar pageName="MSISDN Prices"></NavBar>
  <v-snackbar
      v-model="snackbarVisible"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
  >
    {{ snackbarMessage }}
    <template v-slot:action>
      <v-btn text color="red" @click="snackbarVisible = false">Close</v-btn>
    </template>
  </v-snackbar>

  <v-container name="MSISDN Prices" style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings/msisdn')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      MSISDN Settings
    </v-btn>
    <v-card>
      <v-card-title>
        MSISDN Prices Management
      </v-card-title>
      <v-card-subtitle>
        <v-btn
            color="primary"
            @click="addNewMsisdnPrice()"
        >
          Add New Price
        </v-btn>
        <v-btn
            class="ml-4"
            color="secondary"
            @click="goToSettings()"
        >
          Settings
        </v-btn>
      </v-card-subtitle>
      <v-card-text>
        <v-data-table
            :headers="headers"
            :items="filteredMsisdnPrices"
            item-value="id"
            class="elevation-1"
            :items-per-page="50"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>Prices List</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-switch
                  v-model="showActiveOnly"
                  :label="showActiveOnly ? 'Active only' : 'All (incl. Terminated)'"
                  color="primary"
                  hide-details
                  density="compact"
                  class="mr-4"
              ></v-switch>
              <v-btn color="primary" @click="fetchMsisdnPrices">Refresh</v-btn>
            </v-toolbar>
          </template>

          <template v-slot:[`item.msisdn_property_type`]="{ item }">
            <v-chip :color="getPropertyTypeColor(item.msisdn_property_type)" small>
              {{ item.msisdn_property_type?.toUpperCase() }}
            </v-chip>
          </template>

          <template v-slot:[`item.price_value`]="{ item }">
            {{ item.price_value }} {{ item.currency_name }}
          </template>

          <template v-slot:[`item.status_name`]="{ item }">
            <v-chip :color="item.status_name === 'active' ? 'success' : 'error'" small>
              {{ item.status_name }}
            </v-chip>
          </template>

          <template v-slot:[`item.action`]="{ item }">
            <template v-if="item.is_editable">
              <v-btn
                  color="primary"
                  size="small"
                  @click="editMsisdnPrice(item)"
              >
                Edit
              </v-btn>
              <v-btn
                  class="ml-2"
                  :color="item.status_name === 'active' ? 'warning' : 'success'"
                  size="small"
                  @click="toggleStatus(item)"
              >
                {{ item.status_name === 'active' ? 'Deactivate' : 'Activate' }}
              </v-btn>
            </template>
            <span v-else class="text-grey">View only</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Edit Dialog -->
    <v-dialog v-model="isEditing" max-width="600px">
      <MsisdnPriceEditor
          :selectedMsisdnPrice="selectedMsisdnPrice"
          :token="token"
          :msisdnClasses="msisdnClasses"
          :billingPeriods="billingPeriods"
          :currencies="currencies"
          :msisdnPriceStatuses="msisdnPriceStatuses"
          @MsisdnPriceSavedEvent="handleUpdateSuccess"
          @MsisdnPriceCancelEvent="closeEditor"
      />
    </v-dialog>

    <!-- Add New Dialog -->
    <v-dialog v-model="showAddNewMsisdnPrice" max-width="600px">
      <AddNewMsisdnPriceEditor
          :token="token"
          :msisdnClasses="msisdnClasses"
          :billingPeriods="billingPeriods"
          :currencies="currencies"
          :msisdnPriceStatuses="msisdnPriceStatuses"
          @MsisdnPriceSavedEvent="handleAddSuccess"
          @MsisdnPriceCancelEvent="closeAddDialog"
      />
    </v-dialog>

  </v-container>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import MsisdnPriceEditor from "@/components/MsisdnPriceEditor.vue";
import AddNewMsisdnPriceEditor from "@/components/AddNewMsisdnPriceEditor.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { log } from "@/composables/useLog";
import { getMsisdnPricesList, updateMsisdnPrice } from "@/composables/useGetMsisdnPricesAPI";

export default {
  components: { NavBar, MsisdnPriceEditor, AddNewMsisdnPriceEditor },
  data() {
    return {
      token: '',
      msisdnPrices: [],
      msisdnClasses: [],
      billingPeriods: [],
      currencies: [],
      msisdnPriceStatuses: [],
      selectedMsisdnPrice: null,
      isEditing: false,
      showAddNewMsisdnPrice: false,
      headers: [
        { title: "ID", key: "id", width: "60px" },
        { title: "Managed By", key: "managed_by_client_name" },
        { title: "Name", key: "name" },
        { title: "MSISDN Class", key: "msisdn_class_name" },
        { title: "Type", key: "msisdn_property_type" },
        { title: "Billing Period", key: "billing_period_name" },
        { title: "Price", key: "price_value" },
        { title: "Status", key: "status_name" },
        { title: "Actions", key: "action", sortable: false, width: "200px" },
      ],
      snackbarVisible: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarTimeout: 2000,
      showActiveOnly: true,
    };
  },
  computed: {
    filteredMsisdnPrices() {
      if (this.showActiveOnly) {
        return this.msisdnPrices.filter(p => p.status_name === 'active');
      }
      return this.msisdnPrices;
    }
  },
  methods: {
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbarVisible = true;
    },
    getPropertyTypeColor(type) {
      switch (type) {
        case 'buy': return 'success';
        case 'rent': return 'info';
        case 'transfer': return 'warning';
        default: return 'grey';
      }
    },
    goToSettings() {
      this.$router.replace({ name: "settings" });
    },
    addNewMsisdnPrice() {
      this.showAddNewMsisdnPrice = true;
    },
    async fetchMsisdnPrices() {
      log("Fetching MSISDN Prices...");
      const response = await getMsisdnPricesList(this.token);
      if (response.error) {
        this.showSnackbar("Error fetching prices: " + response.message, "error");
      } else {
        this.msisdnPrices = response.msisdn_prices || [];
        this.msisdnClasses = response.msisdn_classes || [];
        this.billingPeriods = response.billing_periods || [];
        this.currencies = response.currencies || [];
        this.msisdnPriceStatuses = response.msisdn_price_statuses || [];
        log("Loaded " + this.msisdnPrices.length + " prices");
      }
    },
    editMsisdnPrice(item) {
      this.selectedMsisdnPrice = { ...item };
      this.isEditing = true;
    },
    async toggleStatus(item) {
      const activeStatus = this.msisdnPriceStatuses.find(s => s.name === 'active');
      const terminatedStatus = this.msisdnPriceStatuses.find(s => s.name === 'terminated');

      if (!activeStatus || !terminatedStatus) {
        this.showSnackbar("Status types not found", "error");
        return;
      }

      const newStatusId = item.status_name === 'active' ? terminatedStatus.id : activeStatus.id;

      const response = await updateMsisdnPrice(this.token, {
        id: item.id,
        msisdn_price_status_id: newStatusId
      });

      if (response.error) {
        this.showSnackbar("Error updating status: " + response.message, "error");
      } else {
        this.showSnackbar("Status updated successfully", "success");
        this.fetchMsisdnPrices();
      }
    },
    handleUpdateSuccess() {
      this.closeEditor();
      this.fetchMsisdnPrices();
      this.showSnackbar("Price updated successfully", "success");
    },
    handleAddSuccess() {
      this.closeAddDialog();
      this.fetchMsisdnPrices();
      this.showSnackbar("Price added successfully", "success");
    },
    closeEditor() {
      this.isEditing = false;
      this.selectedMsisdnPrice = null;
    },
    closeAddDialog() {
      this.showAddNewMsisdnPrice = false;
    },
  },
  created() {
    const { getLoggedIn, token } = useGetLoggedIn();
    getLoggedIn().then(() => {
      log("MsisdnPricesSettings created");
      log("token: " + token.value);
      this.token = token.value;
      this.fetchMsisdnPrices();
    });
  },
};
</script>
